import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import * as Analytics from 'analytics/analytics'
import * as InteractionStudioService from 'api/interactionStudio'

import { productClickViewDetails } from 'actions/AnalyticsActions'
import { replaceWithRegion } from 'actions/NavigationActions'
import { searchResultDealClick, searchSessionDealClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Pane from 'components/Common/Pane'
import TextButton from 'components/Luxkit/Button/TextButton'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import type { SearchFormRef } from 'components/Search'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'
import SearchResultsMap from 'components/Search/SearchResultsMap/SearchResultsMap'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ALL_HOTEL_TYPES, HOTEL_SEARCH_TYPES, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext, useGlobalHotelsSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useOfferList } from 'hooks/Offers/useOfferList'
import useLastNonEmptyValue from 'hooks/useLastNonEmptyValue'
import useQueryParams from 'hooks/useQueryParams'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { EmptyArray, partitionBy } from 'lib/arrayUtils'
import { decodeSearchParams, encodeSearchParams, getSearchTypeFromFilters } from 'lib/searchUtils'
import { parseSearchString } from 'lib/urlUtils'
import { getOfferListKeySelector } from 'selectors/offerSelectors'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { bezierCurve } from 'styles/tools/constants'
import zIndex from 'styles/tools/z-index'
import HotelSearchHeader from './HotelSearchHeader'
import { searchMapClickBedbankOffer, searchMapClickFlashOffer, searchMapClickLpcOffer, searchMapClickOffer } from 'analytics/optimizely/events'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
`

const OfferContainer = styled.div`
  max-width: fit-content;
  position: absolute;
  display: flex;
  padding: 0 ${rem(16)};
  padding-bottom: ${rem(16)};
  bottom: 0;
  transform: translateY(100%);
  left: 0;
  right: 0;
  z-index: ${zIndex.min};
  pointer-events: none;
  transition: transform .3s ${bezierCurve};
  margin: auto;

  &.show {
    pointer-events: auto;
    transform: translateY(0);
  }

  ${mediaQueryUp.sm} {
    justify-content: center;
  }
`

const FloatingBanner = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.primary.normal};
  margin-left: ${rem(12)};
`

interface Props {
  filters: App.OfferListFilters;
  windowSearch: string;
  isLoggedIn: boolean;
  regionCode: string;
  offerListKey: string;
  searchSessionId: string;
  replaceWithRegion: Utils.ActionDispatcher<typeof replaceWithRegion>,
  productClickViewDetails: Utils.ActionDispatcher<typeof productClickViewDetails>,
}

type SelectedOfferIds = Array<{
  kind: 'le' | 'bedbank',
  id: string,
}>

export function encodeOfferIds(offers: Array<App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary>) {
  return offers
    .map(offer => offer.type === OFFER_TYPE_BED_BANK ? `bedbank:${offer.id}` : `le:${offer.id}`)
    .join(',')
}

function decodeOfferIds(idsParam: string): SelectedOfferIds {
  if (idsParam) {
    return idsParam
      .split(',')
      .map(idWithKind => {
        const [kind, id] = idWithKind.split(':')
        return { kind: kind as 'le' | 'bedbank', id }
      })
  }
  return []
}

function HotelSearchMapPage(props: Props) {
  const {
    filters,
    windowSearch,
    searchSessionId,
    replaceWithRegion,
    productClickViewDetails,
    offerListKey,
  } = props
  const params = parseSearchString(windowSearch)
  const listViewURL = useMemo(() => `/search${windowSearch}`, [windowSearch])

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds)
  ), [params.selectedOfferIds])

  const anyOffersSelected = selectedOfferIds.length > 0

  // Holds the last selected offer IDs, so we can keep showing them while the carousel transitions out of the viewport
  const lastSelectedOfferIds = useLastNonEmptyValue(selectedOfferIds) ?? EmptyArray

  const [lastSelectedLeOfferIds, lastSelectedBedbankOfferIds] = useMemo(() => (
    partitionBy(lastSelectedOfferIds, idWithKind => idWithKind.kind === 'le')
      .map(idsWithKinds => idsWithKinds.map(idWithKind => idWithKind.id))
  ), [lastSelectedOfferIds])

  // Handle adding selected offer IDs to URL
  const setSelectedOffersURLParams = useCallback((offers: Array<App.Offer | App.BedbankOffer>) => {
    const newParams = new URLSearchParams(windowSearch)
    if (offers.length > 0) {
      newParams.set('selectedOfferIds', encodeOfferIds(offers))
    } else {
      newParams.delete('selectedOfferIds')
    }
    const newSearchString = newParams.toString()
    if (newSearchString !== windowSearch) {
      replaceWithRegion('/search/map', newSearchString)
    }
  }, [replaceWithRegion, windowSearch])

  // Handle deselecting offers
  const onDeselectOffers = useCallback(() => {
    setSelectedOffersURLParams([])
  }, [setSelectedOffersURLParams])

  // decodeSearchParams can change depending on the current date, so it's not enough to
  // just use useMemo with windowSearch as the dependency.
  // Better to evaluate on each render and then stablise it with useStableObjectReference.
  const searchParams = useStableObjectReference(decodeSearchParams(windowSearch))

  const dates = useMemo(() => ({
    checkIn: searchParams.dates?.checkIn,
    checkOut: searchParams.dates?.checkOut,
  }), [searchParams])
  const rooms = searchParams.rooms

  // Handle bounds search
  const onBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }

    const searchItem: App.SearchItem = {
      searchType: 'bounds',
      value: newBounds.toUrlValue(),
      format: {
        mainText: 'Search area',
      },
    }

    const searchStr = encodeSearchParams({
      urlSearch: windowSearch,
      searchItem,
      dates,
      rooms,
      filters,
      flexibleNights: filters?.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: filters?.flexibleMonths,
      userSelectedFlexibleMonths: true,
    }).toString()
    replaceWithRegion('/search/map', searchStr)
  }, [dates, replaceWithRegion, rooms, windowSearch, filters])

  const formRef = useRef<SearchFormRef>(null)

  const searchType = getSearchTypeFromFilters(filters)

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.openDatePickerMenu()
  }, [])

  // Handle errors and zero results
  const [modal, setModal] = useState<'error' | 'offline' | 'zeroResults' | null>(null)
  const clearModal = useCallback(() => setModal(null), [])

  const onError = useCallback(() => {
    if (typeof window.navigator.onLine === 'boolean' && !window.navigator.onLine) {
      setModal('offline')
    } else {
      setModal('error')
    }
  }, [])

  const onZeroResults = useCallback(() => {
    if (!anyOffersSelected) {
      setModal('zeroResults')
    }
  }, [anyOffersSelected])

  const onClickModifySearch = useCallback(() => {
    clearModal()
    setTimeout(() => { formRef.current?.focusLocationInput() }, 250)
  }, [clearModal])

  const handleProductClick = useCallback((offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary, position: number) => {
    addGTMEvent(searchResultDealClick())
    addGTMEvent(searchSessionDealClick(searchSessionId, offer.id))
    InteractionStudioService.trackOfferListClick({
      offerId: offer.id,
      index: position,
      list: 'hotels-search-map',
      listId: searchSessionId,
    })
    productClickViewDetails(offer)

    const mapTileClickEvent = searchMapClickOffer()

    Analytics.track(mapTileClickEvent.eventName, mapTileClickEvent)

    if (offer.type === OFFER_TYPE_HOTEL) {
      const mapTileClickFlashEvent = searchMapClickFlashOffer()
      Analytics.track(mapTileClickFlashEvent.eventName, mapTileClickFlashEvent)
    }

    if (offer.type === OFFER_TYPE_ALWAYS_ON) {
      const mapTileClickLpcEvent = searchMapClickLpcOffer()
      Analytics.track(mapTileClickLpcEvent.eventName, mapTileClickLpcEvent)
    }

    if (offer.type === OFFER_TYPE_BED_BANK) {
      const mapTileClickBedbankEvent = searchMapClickBedbankOffer()
      Analytics.track(mapTileClickBedbankEvent.eventName, mapTileClickBedbankEvent)
    }
  }, [productClickViewDetails, searchSessionId])

  const searchTargetLabel = (params.landmarkName || params.destinationName)

  const queryParams = useQueryParams()

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    onBedbankSelectDatesPrompt,
    searchTargetLabel,
  }, queryParams)

  const listFilters = useMemo((): App.OfferListFilters => ({ ...filters, offerTypes: [...ALL_HOTEL_TYPES] }), [filters])
  const nearbyPropertyListFilters = useMemo((): App.OfferListFilters => ({ ...filters, offerTypes: HOTEL_SEARCH_TYPES, searchNearby: true }), [filters])

  const [offerList] = useOfferList(listFilters)
  const [nearbyPropertyOfferList] = useOfferList(nearbyPropertyListFilters, { disabled: !filters.propertyId })

  const allHotelLists = useMemo(() => [offerList], [offerList])
  const allNearbyHotelLists = useMemo(() => [nearbyPropertyOfferList], [nearbyPropertyOfferList])

  useGlobalHotelsSearchResultsTracking(
    globalHotelsSearchState,
    allHotelLists,
    offerListKey,
    !filters.propertyId ? allNearbyHotelLists : undefined,
  )

  const offerListFiltersProps = {
    searchType,
    switchView: {
      label: 'List view',
      view: 'list' as const,
      URL: listViewURL,
    },
    listFilters,
  }

  return <GlobalSearchContextProvider
    state={globalHotelsSearchState}
    dispatch={globalHotelsSearchDispatch}
  >
    <Root>
      <Pane shadow>
        <HotelSearchHeader
          filters={filters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
        />
      </Pane>
      <MapWrapper>
        <SearchResultsMap
          filters={filters}
          onBoundsChanged={onBoundsChanged}
          onSelectOffers={setSelectedOffersURLParams}
          onDeselectOffers={onDeselectOffers}
          selectedOfferIds={selectedOfferIds}
          onError={onError}
          onZeroResults={onZeroResults}
        />

        {offerList.fetching && <FloatingBanner>
          <TextButton
            kind="secondary"
            variant="dark"
            nonInteractable={offerList.fetching}
            endIcon={<ColourfulLoadingAnimation
            size={8}/>}>
            Loading offers
          </TextButton>
        </FloatingBanner>}

        <OfferContainer className={cn({ show: anyOffersSelected })}>
          <SearchResultsMapOfferView
            key={JSON.stringify(lastSelectedOfferIds)}
            leOfferIds={lastSelectedLeOfferIds}
            bedbankOfferIds={lastSelectedBedbankOfferIds}
            filters={filters}
            onProductClick={handleProductClick}
            onDeselectOffers={onDeselectOffers}
          />
        </OfferContainer>
      </MapWrapper>

    </Root>

    <Modal
        isOpen={modal === 'error'}
        onClose={clearModal}
        title="Looks like something's gone wrong"
        primaryActionText="Refresh search"
        onPrimaryActionClick={() => window.location.reload()}
        secondaryActionText="Modify search"
        onSecondaryActionClick={onClickModifySearch}
      >
      <BodyText variant="large">Refresh or modify your search to see available offers.</BodyText>
    </Modal>

    <Modal
      isOpen={modal === 'offline'}
      onClose={clearModal}
      title="Looks like you’re offline"
      primaryActionText="OK"
      onPrimaryActionClick={clearModal}
    >
      <BodyText variant="large">Connect to the internet to search this area.</BodyText>
    </Modal>

    <Modal
      isOpen={modal === 'zeroResults'}
      onClose={clearModal}
      title="We couldn’t find any offers that match your search criteria"
      primaryActionText="Modify search"
      onPrimaryActionClick={onClickModifySearch}
    >
      <BodyText variant="large">Please modify your search and try again.</BodyText>
    </Modal>
  </GlobalSearchContextProvider>
}

const mapStateToProps = (state: App.State) => {
  const filters = getUrlOfferListFilters(state)

  const offerListParams = {
    filters,
    types: HOTEL_SEARCH_TYPES,
  }

  const offerListKey = getOfferListKeySelector(state, offerListParams)

  return {
    windowSearch: state.router.location.search,
    filters,
    offerListKey,
    regionCode: state.geo.currentRegionCode,
    searchSessionId: state.analytics.search.sessions[offerListKey],
  }
}

const mapDispatchToProps = {
  replaceWithRegion,
  productClickViewDetails,
}

export default connect(mapStateToProps, mapDispatchToProps)(HotelSearchMapPage)
